import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  BellRingIcon,
  CalendarClockIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  PauseCircleIcon,
  PlayIcon,
  PlusIcon,
  RefreshCwIcon,
  TrophyIcon,
  VideoIcon,
  VideoOffIcon,
} from 'lucide-react';
import { z } from 'zod';
import {
  DRAW_MAX_WINNERS,
  DRAW_REMINDER_LEAD_MINUTES,
  DrawStatus,
  DrawType,
  scheduleDrawRequestSchema,
  type AdminDraw,
} from '@qaytim/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DatePicker } from '@/components/date-picker';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { PageHeader } from '@/components/page-header';
import { useAdminForm } from '@/lib/admin-form';
import { PagerControls } from '@/components/pager';
import { StatCard } from '@/components/stat-card';
import { useOffsetPager } from '@/hooks/use-offset-pager';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice, SuccessNotice, TableSkeleton } from '@/components/states';
import {
  usePostponeDraw,
  useRunDraw,
  useScheduleDraw,
  useSetDrawLive,
  useAdminDraws,
  type RunDrawInput,
} from '@/lib/admin-queries';
import { formatNumber } from '@/lib/utils';
import { formatTashkent, tashkentLocalInputToIso } from '@/lib/time';
import { strings } from '@/i18n/strings';

const STATUS_LABEL: Record<string, string> = {
  [DrawStatus.SCHEDULED]: strings.draws.statusScheduled,
  [DrawStatus.COMPLETED]: strings.draws.statusCompleted,
  [DrawStatus.POSTPONED]: strings.draws.statusPostponed,
};

/**
 * Jadvalga qo'yish formasining sxemasi.
 *
 * Forma maydonlari so'rov shakliga TO'G'RI kelmaydi: efir vaqti panelda Toshkent devor-soati
 * sifatida kiritiladi va ISO ga o'giriladi, davr esa turga qarab ikkita maydondan biridan
 * olinadi. Shuning uchun sxema ikki qavatli — xom maydonlar `transform` bilan so'rovga
 * aylanadi, keyin SHARTNOMANING o'zi (`scheduleDrawRequestSchema`) uni tekshiradi.
 *
 * Bu shakl ataylab: shartnomadagi `superRefine` "kunlik uchun sana, oylik uchun oy" qoidasini
 * saqlaydi, va uning xatolari `drawDate`/`monthKey` yo'llari bilan keladi — ya'ni ular
 * to'g'ri maydon ostida ko'rinadi. Qoidani formada takrorlash ikkinchi nusxa bo'lardi, va
 * bunday nusxalar aynan shu joyda ajralib ketishga moyil.
 */
const drawFormSchema = z
  .object({
    type: z.nativeEnum(DrawType),
    drawDate: z.string(),
    monthKey: z.string(),
    scheduledAt: z.string(),
    prizeLabel: z.string(),
    videoEmbedUrl: z.string(),
    winnerCount: z.string(),
    /**
     * Har bir g'olibning sovrini. Uzunligi `winnerCount` ga ergashadi, lekin maydonning O'ZI
     * uzunroq bo'lishi mumkin — operator g'oliblar sonini kamaytirsa, yozilganlar yo'qolib
     * ketmasligi uchun. Kesish `transform` da.
     */
    prizes: z.array(z.string()),
  })
  .transform((values, ctx) => {
    /*
      Ikkala tekshiruv ham BIRGA yig'iladi va faqat oxirida to'xtatiladi. Birinchisida
      qaytib ketish operatorni xatolarni birma-bir tuzatib, formani qayta-qayta yuborishga
      majbur qilardi — ikkalasi ham bir vaqtda ko'rinishi kerak.
    */
    // Operator Toshkent vaqtini kiritadi — brauzer qaysi zonada bo'lishidan qat'i nazar.
    const scheduledAt = tashkentLocalInputToIso(values.scheduledAt);
    if (scheduledAt === '') {
      // Bo'sh natija — vaqt o'qib bo'lmadi degani. Xato aynan SHU maydonga bog'lanadi.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledAt'],
        message: strings.draws.formInvalid,
      });
    }

    /*
      Davr bo'shligi SHU YERDA ham tekshiriladi, garchi shartnomaning `superRefine` i uni
      baribir ushlasa. Sabab tarjima: shartnoma paketi tilga bog'liq emas va uning matni
      ingliz tilida ("Required for a daily draw") — u operatorning ekraniga chiqib ketardi.
      Bu tekshiruv QOIDANI takrorlamaydi (u "qaysi maydon kerak" ni turdan o'qiydi, xuddi
      formaning o'zi kabi), faqat eng ko'p uchraydigan xatoni o'zbekcha aytadi.
    */
    // Kunlik va HAFTALIK efir kun bilan kalitlanadi (haftalikda — o'sha haftaning dushanbasi),
    // oylik esa oy bilan. Bazadagi `draws_period_key` CHECK i bilan bir xil bo'linish.
    const usesDay = values.type !== DrawType.MONTHLY;
    const periodValue = usesDay ? values.drawDate : values.monthKey;
    if (periodValue === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [usesDay ? 'drawDate' : 'monthKey'],
        message: strings.draws.periodRequired,
      });
    }

    if (scheduledAt === '' || periodValue === '') return z.NEVER;

    /*
      Haftalik efirning davri faqat dushanba bo'lishi mumkin. Shartnoma ham, baza ham buni rad
      etadi — bu yerda takrorlanishining yagona sababi til: shartnomaning matni inglizcha va u
      operatorning ekraniga chiqib ketardi.
    */
    if (values.type === DrawType.WEEKLY && !isMonday(periodValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['drawDate'],
        message: strings.draws.weekMustBeMonday,
      });
      return z.NEVER;
    }

    /*
      Sovrin ro'yxati faqat BIR NECHTA g'olibli efirda ma'noga ega: bitta g'olibda ro'yxatning
      yagona qatori yuqoridagi umumiy sovrinning nusxasi bo'lardi, ya'ni operatordan bir xil
      matnni ikki marta yozishni so'rardi.
    */
    const count = values.winnerCount.trim() === '' ? undefined : Number(values.winnerCount);
    const wanted = count !== undefined && count >= 2 ? count : 0;
    const entered = values.prizes.slice(0, wanted).map((prize) => prize.trim());
    const anyFilled = entered.some((prize) => prize !== '');

    /*
      Yarim to'ldirilgan ro'yxat — eng qimmat xato: server bo'sh qolgan g'olibni umumiy sovringa
      tushiradi va buni faqat log ko'radi. Shartnoma ham buni rad etardi, ammo uning matni
      inglizcha.
    */
    if (anyFilled && entered.some((prize) => prize === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prizes'],
        message: strings.draws.prizesIncomplete,
      });
      return z.NEVER;
    }

    return {
      type: values.type,
      ...(usesDay ? { drawDate: periodValue } : { monthKey: periodValue }),
      scheduledAt,
      prizeLabel: values.prizeLabel.trim(),
      ...(values.videoEmbedUrl.trim() === '' ? {} : { videoEmbedUrl: values.videoEmbedUrl.trim() }),
      // Bo'sh qoldirilsa yuborilmaydi — mavjud qiymat tegilmaydi, yangi qator uchun esa 1.
      ...(values.winnerCount.trim() === '' ? {} : { winnerCount: Number(values.winnerCount) }),
      // Har doim yuboriladi: bo'sh massiv "ro'yxatni tozalash" degani, ya'ni efirni qaytadan
      // jadvalga qo'yish eski ro'yxatni jimgina saqlab qolmaydi.
      prizes: anyFilled ? entered : [],
    };
  })
  .pipe(scheduleDrawRequestSchema);

const EMPTY_DRAW_FORM = {
  type: DrawType.DAILY,
  drawDate: '',
  monthKey: '',
  scheduledAt: '',
  prizeLabel: '',
  videoEmbedUrl: '',
  winnerCount: '1',
  prizes: [] as string[],
} as const;

const TABLE_COLUMNS = 8;

/**
 * Tortishuv boshqaruvi (master-promt §5 P3.5).
 *
 * Ekran ataylab jadval atrofida qurilgan: efirni o'tkazish tugmasi mavjud QATOR ustida turadi,
 * ya'ni tortishuv o'tkazilishidan oldin u jadvalga qo'yilgan bo'ladi. Bu shunchaki tartib emas —
 * eslatma xabari faqat oldindan yaratilgan `scheduled_at` qatori uchun yuboriladi, ya'ni "avval
 * jadval, keyin efir" ketma-ketligi mahsulotning o'zi.
 */
export function DrawsPage() {
  const pager = useOffsetPager();
  const draws = useAdminDraws({ page: pager.page, limit: pager.limit });
  const schedule = useScheduleDraw();
  const run = useRunDraw();
  const postpone = usePostponeDraw();
  const setLive = useSetDrawLive();

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const form = useAdminForm({ schema: drawFormSchema, defaultValues: EMPTY_DRAW_FORM });
  const type = form.watch('type');

  /**
   * Nechta sovrin maydoni chizilishi.
   *
   * `winnerCount` matn maydoni, ya'ni u vaqtincha bo'sh yoki chegaradan tashqarida bo'lishi
   * mumkin (operator raqamni o'chirib, qaytadan yozayotgan payt). Bunday oraliq holatda
   * ro'yxatni yo'q qilib yubormaslik uchun qiymat chegaraga qisiladi, xato esa formaning o'z
   * tekshiruvida chiqadi.
   */
  const winnerCountRaw = Number(form.watch('winnerCount'));
  const prizeSlots =
    Number.isFinite(winnerCountRaw) && winnerCountRaw >= 2
      ? Math.min(Math.floor(winnerCountRaw), DRAW_MAX_WINNERS)
      : 0;

  /** Davr maydonlari tozalanadi, tur esa kunlikka qaytadi — oyna har safar bir xil ochiladi. */
  const resetForm = () => {
    form.reset(EMPTY_DRAW_FORM);
  };

  const submitSchedule = form.handleSubmit((body) => {
    schedule.mutate(body, {
      // Muvaffaqiyatdan keyin oyna yopiladi va davr maydonlari tozalanadi: eng qimmat xato —
      // o'sha kunni ikkinchi marta jadvalga qo'yish, va to'ldirilgan forma aynan shunga undaydi.
      onSuccess: () => {
        setScheduleOpen(false);
        resetForm();
      },
      onError: form.applyServerError,
    });
  });

  const items = draws.data?.items;
  const rows = useMemo(() => items ?? [], [items]);

  /*
    Sanoqlar SERVERDAN keladi, `rows` dan hisoblanmaydi. Sahifalash kiritilgach mahalliy
    hisob jimgina "shu sahifadagi to'rttadan ikkitasi jadvalda" degan ma'noga o'tib ketardi —
    ekranda o'zgarishsiz ko'rinib turib.
  */
  const counts = draws.data?.statusCounts;
  const stats = useMemo(
    () => ({
      total: draws.data?.total ?? 0,
      scheduled: counts?.[DrawStatus.SCHEDULED] ?? 0,
      completed: counts?.[DrawStatus.COMPLETED] ?? 0,
      postponed: counts?.[DrawStatus.POSTPONED] ?? 0,
    }),
    [counts, draws.data?.total],
  );

  const busy = run.isPending || postpone.isPending || setLive.isPending;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={strings.draws.title}
        description={strings.draws.subtitle}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void draws.refetch()}
              loading={draws.isFetching}
            >
              {draws.isFetching ? null : <RefreshCwIcon aria-hidden="true" />}
              {draws.isFetching ? strings.common.refreshing : strings.common.refresh}
            </Button>

            <FormDialog
              open={scheduleOpen}
              onOpenChange={(open) => {
                setScheduleOpen(open);
                if (!open) resetForm();
              }}
              trigger={
                <Button>
                  <PlusIcon aria-hidden="true" />
                  {strings.draws.scheduleHeading}
                </Button>
              }
              title={strings.draws.scheduleHeading}
              description={strings.draws.scheduleHint.replace(
                '{minutes}',
                String(DRAW_REMINDER_LEAD_MINUTES),
              )}
              submitLabel={schedule.isPending ? strings.draws.saving : strings.draws.save}
              submitting={schedule.isPending}
              onSubmit={submitSchedule}
              error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : null}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={strings.draws.type}>
                  <Select {...form.register('type')}>
                    <option value={DrawType.DAILY}>{strings.draws.typeDaily}</option>
                    <option value={DrawType.WEEKLY}>{strings.draws.typeWeekly}</option>
                    <option value={DrawType.MONTHLY}>{strings.draws.typeMonthly}</option>
                  </Select>
                </Field>

                {type !== DrawType.MONTHLY ? (
                  <Field
                    // Bir xil maydon, boshqa ma'no: haftalik efirda bu — o'sha haftaning
                    // dushanbasi, efir kuni emas (efir vaqti quyidagi `scheduledAt` da).
                    label={
                      type === DrawType.WEEKLY ? strings.draws.weekKey : strings.draws.drawDate
                    }
                    error={form.formState.errors.drawDate?.message}
                  >
                    <DatePicker
                      name="drawDate"
                      ariaLabel={
                        type === DrawType.WEEKLY ? strings.draws.weekKey : strings.draws.drawDate
                      }
                      value={form.watch('drawDate')}
                      onChange={(value) => form.setValue('drawDate', value)}
                    />
                  </Field>
                ) : (
                  <Field
                    label={strings.draws.monthKey}
                    error={form.formState.errors.monthKey?.message}
                  >
                    <DatePicker
                      mode="month"
                      name="monthKey"
                      ariaLabel={strings.draws.monthKey}
                      value={form.watch('monthKey')}
                      onChange={(value) => form.setValue('monthKey', value)}
                    />
                  </Field>
                )}

                <Field label={strings.draws.scheduledAt}>
                  {/*
                    Efir vaqti — panelning `datetime` rejimi. Bu maydonning qiymati
                    `tashkentLocalInputToIso` ga boradi, ya'ni u Toshkent devor-soati sifatida
                    o'qiladi; panel ham aynan shu zonada chizilgani uchun ekranda ko'ringan kun
                    bilan yuborilgan kun bir xil bo'ladi.
                  */}
                  <DatePicker
                    mode="datetime"
                    name="scheduledAt"
                    ariaLabel={strings.draws.scheduledAt}
                    value={form.watch('scheduledAt')}
                    onChange={(value) => form.setValue('scheduledAt', value)}
                  />
                </Field>

                <Field
                  label={strings.draws.prizeLabel}
                  error={form.formState.errors.prizeLabel?.message}
                >
                  <Input {...form.register('prizeLabel')} />
                </Field>

                <Field
                  label={strings.draws.winnerCount}
                  hint={strings.draws.winnerCountHint}
                  error={form.formState.errors.winnerCount?.message}
                >
                  <Input
                    type="number"
                    min={1}
                    max={DRAW_MAX_WINNERS}
                    {...form.register('winnerCount')}
                  />
                </Field>

                {/*
                  Faqat bir nechta g'olibli efirda: bitta g'olibda ro'yxatning yagona qatori
                  yuqoridagi umumiy sovrinning nusxasi bo'lardi.
                */}
                {prizeSlots > 0 && (
                  <Field
                    label={strings.draws.prizes}
                    hint={strings.draws.prizesHint}
                    error={form.formState.errors.prizes?.message}
                    className="sm:col-span-2"
                  >
                    <div className="grid gap-2 sm:grid-cols-2">
                      {Array.from({ length: prizeSlots }, (_, index) => (
                        <Input
                          key={index}
                          aria-label={strings.draws.prizeAt.replace('{n}', String(index + 1))}
                          placeholder={strings.draws.prizeAt.replace('{n}', String(index + 1))}
                          value={form.watch(`prizes.${index}`) ?? ''}
                          onChange={(event) =>
                            form.setValue(`prizes.${index}`, event.currentTarget.value)
                          }
                        />
                      ))}
                    </div>
                  </Field>
                )}

                <Field
                  label={strings.draws.videoEmbedUrl}
                  hint={strings.draws.videoHint}
                  error={form.formState.errors.videoEmbedUrl?.message}
                  className="sm:col-span-2"
                >
                  <Input {...form.register('videoEmbedUrl')} />
                </Field>
              </div>
            </FormDialog>
          </>
        }
      />

      <div className="motion-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={strings.draws.statTotal}
          value={formatNumber(stats.total)}
          icon={CalendarClockIcon}
        />
        <StatCard
          label={strings.draws.statScheduled}
          value={formatNumber(stats.scheduled)}
          icon={CalendarClockIcon}
        />
        <StatCard
          label={strings.draws.statCompleted}
          value={formatNumber(stats.completed)}
          icon={CheckCircle2Icon}
        />
        <StatCard
          label={strings.draws.statPostponed}
          value={formatNumber(stats.postponed)}
          icon={PauseCircleIcon}
        />
      </div>

      {/*
        Tasdiq oynadan TASHQARIDA: oyna muvaffaqiyatdan keyin yopiladi, ya'ni ichidagi xabar
        ko'rinmasdan yo'q bo'lardi. Xato esa ichida qoladi — u formani tuzatishni talab qiladi.
      */}
      {schedule.isSuccess && !scheduleOpen ? (
        <SuccessNotice>{strings.draws.saved}</SuccessNotice>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{strings.draws.listHeading}</CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {draws.isError ? <ErrorNotice error={draws.error} /> : null}
          {run.isError ? <ErrorNotice error={run.error} /> : null}
          {postpone.isError ? <ErrorNotice error={postpone.error} /> : null}
          {setLive.isError ? <ErrorNotice error={setLive.error} /> : null}

          {draws.isSuccess && rows.length === 0 ? (
            <EmptyState title={strings.draws.empty} icon={TrophyIcon} />
          ) : (
            /*
             * Jadvalning eng kichik kengligi belgilangan: tor ekranda u SIQILMAYDI, aylanadi.
             * Siqilganda brauzer o'ralishi mumkin bo'lgan yagona ustunni — "G'olib" ni — bir
             * necha pikselgacha ezib qo'yardi, ya'ni g'olibning nomi o'qilmay qolardi.
             * Aylanish borligi jadval chekkasidagi soyadan ko'rinadi (`table-scroll`).
             */
            <Table className="min-w-[64rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{strings.draws.type}</TableHead>
                  <TableHead>{strings.draws.columnPeriod}</TableHead>
                  <TableHead>{strings.draws.columnStatus}</TableHead>
                  <TableHead>{strings.draws.columnPrize}</TableHead>
                  <TableHead>{strings.draws.columnScheduledAt}</TableHead>
                  <TableHead className="text-right">{strings.draws.columnTickets}</TableHead>
                  <TableHead>{strings.draws.columnWinner}</TableHead>
                  {/*
                    Amallar ustuni o'ngga yopishtirilgan: jadval kengligi ekrandan oshganda
                    birinchi bo'lib ko'rinmay qoladigan narsa aynan tugmalar bo'lardi — ya'ni
                    ekranning yagona ish bajaradigan qismi.
                  */}
                  <TableHead className="md:border-border md:bg-card md:sticky md:right-0 md:border-l md:shadow-[-10px_0_10px_-10px_var(--table-edge-shadow)]">
                    {strings.draws.columnActions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draws.isPending ? <TableSkeleton columns={TABLE_COLUMNS} /> : null}
                {rows.map((draw) => (
                  <DrawRow
                    key={draw.id}
                    draw={draw}
                    onRun={() =>
                      // Sovrin HECH QAYSI turda yuborilmaydi: qator allaqachon mavjud (tugma
                      // o'sha qatorning ustida turibdi), ya'ni o'tkazish bosqichi unga baribir
                      // tegmaydi. Yuborish "efirni o'tkazish sovrinni ham yozadi" degan noto'g'ri
                      // taassurot qoldirardi — sovrinning yagona egasi jadvalga qo'yish formasi.
                      run.mutate(runInputFor(draw))
                    }
                    onPostpone={(reason) => postpone.mutate({ drawId: draw.id, reason })}
                    onSetLive={(enabled) => setLive.mutate({ drawId: draw.id, enabled })}
                    busy={busy}
                  />
                ))}
              </TableBody>
            </Table>
          )}

          <PagerControls
            pager={pager}
            total={draws.data?.total}
            pageCount={draws.data?.pageCount}
            rowCount={rows.length}
            busy={draws.isFetching}
            label={strings.draws.listHeading}
          />
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * Efir eslatmasini o'z chatingizga yuborib ko'rish.
 *
 * Tortishuvlar sahifasida turadi, chunki eslatma efirning bir qismi: operator jadvalga qo'yishdan
 * oldin xabar qanday ko'rinishini bilishi kerak — u butun bazaga ketadigan yagona xabar va uni
 * "jonli" sinab ko'rib bo'lmaydi.
 */
interface DrawRowProps {
  draw: AdminDraw;
  onRun: () => void;
  onPostpone: (reason?: string) => void;
  onSetLive: (enabled: boolean) => void;
  busy: boolean;
}

function DrawRow({ draw, onRun, onPostpone, onSetLive, busy }: DrawRowProps) {
  const [reason, setReason] = useState('');
  const period = draw.drawDate ?? draw.monthKey ?? '';
  const completed = draw.status === DrawStatus.COMPLETED;

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">{DRAW_TYPE_LABEL[draw.type]}</TableCell>
      <TableCell className="tabular font-medium whitespace-nowrap">{period}</TableCell>
      <TableCell className="whitespace-nowrap">
        <span className="flex items-center gap-1.5">
          <StatusBadge status={draw.status}>{STATUS_LABEL[draw.status]}</StatusBadge>
          {/*
            Belgi faqat rasm emas: yonidagi ko'rinmas matn bo'lmasa, eslatma yuborilgani ekran
            o'quvchisi bilan ishlaydigan operatorga umuman yetib bormasdi.
          */}
          {draw.reminderQueuedAt ? (
            <>
              <BellRingIcon className="text-muted-foreground size-3.5" aria-hidden="true" />
              <span className="sr-only">{strings.draws.reminderSent}</span>
            </>
          ) : null}
          {/* Yashirilgan efir holat ustunida ko'rinadi: tugmalar ustuni ekrandan chiqib
              ketganda ham operator qaysi qator mini appda ko'rinmasligini bilishi kerak. */}
          {draw.liveEnabled ? null : (
            <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
              <VideoOffIcon className="size-3.5" aria-hidden="true" />
              {strings.draws.liveHiddenBadge}
            </span>
          )}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          {draw.prizeLabel}
          {draw.videoEmbedUrl ? (
            <a
              href={draw.videoEmbedUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
            >
              <ExternalLinkIcon className="size-3" aria-hidden="true" />
              {strings.draws.openVideo}
            </a>
          ) : null}
        </span>
      </TableCell>
      <TableCell className="tabular whitespace-nowrap">
        {formatTashkent(draw.scheduledAt)}
      </TableCell>
      <TableCell className="tabular text-right">
        {draw.ticketCount === null ? strings.draws.noWinner : formatNumber(draw.ticketCount)}
      </TableCell>
      {/* G'olib matni uzun bo'lishi mumkin — u o'ralib ketsin, ustun esa siqilib qolmasin. */}
      <TableCell className="min-w-44 whitespace-nowrap">
        {draw.winners.length === 0 ? (
          strings.draws.noWinner
        ) : (
          // Birinchisi to'liq, qolganlari sanoq bilan: uchta g'olib jadval qatorini yorib
          // yuborardi, konsolda esa ularning hammasi baribir ko'rinadi.
          <span>
            {`${draw.winners[0]?.ticketCode} · ${draw.winners[0]?.userDisplayName}`}
            {draw.winners.length > 1 ? (
              <span className="text-muted-foreground block text-xs">
                {strings.draws.winnersMore.replace('{count}', String(draw.winners.length - 1))}
              </span>
            ) : null}
          </span>
        )}
      </TableCell>
      {/*
        Amallar ustuni o'ngga YOPISHIB turadi: jadval gorizontal aylanadi (`min-w` ostida), va
        aylanish paytida "O'tkazish" tugmasi ekrandan chiqib ketmasligi kerak. Soya endi
        mavzu o'zgaruvchisidan — qattiq yozilgan `rgba(0,0,0,.12)` qorong'i mavzuda umuman
        ko'rinmasdi, ya'ni yopishgan ustun mazmun ustida "suzib" turgani bilinmasdi.
      */}
      <TableCell className="md:border-border md:bg-card md:sticky md:right-0 md:border-l md:shadow-[-10px_0_10px_-10px_var(--table-edge-shadow)]">
        {/*
          Ikki qator: yuqorida efirni o'tkazish, pastda qoldirish sababi bilan. Bitta qatorga
          tiqilganda jadval ekrandan chiqib ketardi va "Biletlar"/"G'olib" ustunlari ko'rinmay
          qolardi — ular esa aynan tugma bosishdan oldin qaraladigan ustunlar.
        */}
        <div className="flex w-fit flex-col items-stretch gap-1.5">
          {/*
            Efir tugmasi HAR QANDAY holatdagi qator ustida turadi, `completed` da ham: u efir
            vaqti xato bo'lgan yoki efir umuman bo'lmagan kuni bosiladi, ya'ni ko'pincha
            tortishuv allaqachon o'tkazilgan bo'ladi.
          */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSetLive(!draw.liveEnabled)}
            disabled={busy}
            title={strings.draws.liveHint}
          >
            {draw.liveEnabled ? (
              <VideoOffIcon aria-hidden="true" />
            ) : (
              <VideoIcon aria-hidden="true" />
            )}
            {draw.liveEnabled ? strings.draws.liveOff : strings.draws.liveOn}
          </Button>

          {/* O'tkazilgan tortishuvni qayta o'tkazib bo'lmaydi — tugmaning o'zi ko'rinmaydi. */}
          {!completed ? (
            <>
              <Button size="sm" onClick={onRun} disabled={busy}>
                <PlayIcon aria-hidden="true" />
                {busy ? strings.draws.running : strings.draws.run}
              </Button>
              <div className="flex items-center gap-1.5">
                <Input
                  className="h-8 w-full min-w-32"
                  placeholder={strings.draws.postponeReason}
                  aria-label={`${strings.draws.postponeReason} — ${period}`}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onPostpone(reason.trim() === '' ? undefined : reason.trim())}
                  disabled={busy}
                >
                  {strings.draws.postpone}
                </Button>
              </div>
            </>
          ) : (
            <Button size="sm" variant="outline" asChild>
              <Link to="/draws/$drawId" params={{ drawId: draw.id }}>
                {strings.draws.console}
              </Link>
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Efir turining ustundagi nomi.
 *
 * `Record` — ya'ni yangi tur qo'shilganda TypeScript shu yerni ko'rsatadi. Ilgari bu ternary
 * edi va uchinchi tur jimgina "Oylik" bo'lib ko'rinardi.
 */
const DRAW_TYPE_LABEL: Record<DrawType, string> = {
  [DrawType.DAILY]: strings.draws.typeDaily,
  [DrawType.WEEKLY]: strings.draws.typeWeekly,
  [DrawType.MONTHLY]: strings.draws.typeMonthly,
};

/** Qaysi endpointga va qaysi davr kaliti bilan borish — turdan kelib chiqadi. */
function runInputFor(draw: AdminDraw): RunDrawInput {
  switch (draw.type) {
    case DrawType.DAILY:
      return { type: DrawType.DAILY, drawDate: draw.drawDate ?? undefined };
    case DrawType.WEEKLY:
      return { type: DrawType.WEEKLY, weekKey: draw.drawDate ?? undefined };
    case DrawType.MONTHLY:
      return { type: DrawType.MONTHLY, monthKey: draw.monthKey ?? undefined };
  }
}

/**
 * `YYYY-MM-DD` dushanbami.
 *
 * `Date.UTC` bilan, mahalliy vaqt zonasisiz: sana kalitining o'zi zonasiz qiymat, va uni
 * brauzerning zonasida o'qish operator qaysi mamlakatda o'tirganiga qarab bir kun surilardi.
 */
function isMonday(dayKey: string): boolean {
  const [year, month, day] = dayKey.split('-').map(Number);
  if (!year || !month || !day) return false;
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() === 1;
}
